import { useMutation, useQuery, useSubscription } from '@apollo/client';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { filterTreeListTopics } from '../topic/toTopicTreeList';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import useAllTopics from '../topic/useAllTopics';
import useMembershipTopics from '../topic/useMembershipTopics';
import { useUserMembershipTopics } from '../user/useUserMembershipTopics';
import isNotDetached from './isNotDetached';
import useExternalPostPlatforms from './useExternalPostPlatforms';
import useSidebarSettings from './useSidebarSettings';

const SECTION_FRAGMENT = gql(`
  # eslint-disable-next-line slab-graphql/no-unused-fields
  fragment SidebarSectionsProvider_section on Section {
    id
    name
    icon {
      preset
    }
    color
    items {
      ... on Post {
        id
      }
      ... on Topic {
        id
        detached
      }
    }
  }
`);

type CreateAction = 'create';
type DeleteAction = 'delete';
type SoftAction = 'update' | 'move' | 'hide' | 'show';

type CreateActionSequence = [CreateAction, ...SoftAction[]];
type DeleteActionSequence = [...SoftAction[], DeleteAction];
type ActionSequence =
  | [SoftAction, ...SoftAction[]]
  | DeleteActionSequence
  | CreateActionSequence;

function hasCreateAction(
  actions: ActionSequence | undefined,
): actions is CreateActionSequence {
  return !!actions && _.first(actions) === 'create';
}

function hasDeleteAction(
  actions: ActionSequence | undefined,
): actions is DeleteActionSequence {
  return !!actions && _.last(actions) === 'delete';
}

export type Section = DocumentType<typeof SECTION_FRAGMENT> & {
  actions?: ActionSequence;
  premade?: boolean;
  visible: boolean;
};

export const PremadeSections = {
  MY_TOPICS: 'my_topics',
  ALL_TOPICS: 'all_topics',
  INTEGRATIONS: 'integrations',
} as const;

type PremadeSectionId = typeof PremadeSections[keyof typeof PremadeSections];

export const isPremadeSectionId = (id: string): id is PremadeSectionId =>
  Object.values(PremadeSections).includes(id as PremadeSectionId);

const SIDEBAR_SECTIONS_MODAL_QUERY = gql(`
  query SidebarSectionsProvider {
    currentSession {
      user {
        id
        sections {
          id
          ...SidebarSectionsProvider_section
        }
        sidebarSections {
          id
          ...SidebarSectionsProvider_section
        }
      }
    }
  }
`);

const SECTION_UPDATED_SUBSCRIPTION = gql(`
  subscription SectionUpdated {
    sectionUpdated {
      id
      ...SidebarSectionsProvider_section
    }
  }
`);

const USER_SETTINGS_UPDATED_SUBSCRIPTION = gql(`
  subscription UserSettingsUpdated {
    userSettingsUpdated {
      sidebar {
        sectionIds
      }
    }
  }
`);

const SECTION_DELETED_SUBSCRIPTION = gql(`
  subscription SectionDeleted {
    sectionDeleted {
      id
      ...SidebarSectionsProvider_section
    }
  }
`);

const UPDATE_SIDEBAR_SECTION_IDS = gql(`
  mutation updateSidebarSectionIds($sectionIds: [ID!]!) {
    updateUserSettings(sidebar: {sectionIds: $sectionIds}) {
      sidebar {
        sectionIds
      }
    }
  }
`);

const CREATE_SIDEBAR_SECTION = gql(`
  mutation createSidebarSection($name: String!, $itemIds: [ID!]!) {
    createSection(name: $name,  itemIds: $itemIds, isTeamSection: false) {
      ...SidebarSectionsProvider_section
    }
  }
`);

const DELETE_SIDEBAR_SECTION = gql(`
  mutation deleteSidebarSection($id: ID!) {
    deleteSection(id: $id) {
      ...SidebarSectionsProvider_section
    }
  }
`);

const UPDATE_SIDEBAR_SECTION = gql(`
  mutation updateSidebarSection($id: ID!, $name: String, $itemIds: [ID!]!) {
    updateSection(id: $id, name: $name, itemIds: $itemIds) {
      ...SidebarSectionsProvider_section
    }
  }
`);

export const useUpdateSidebarSection = () =>
  useMutation(UPDATE_SIDEBAR_SECTION);

const UPDATE_MY_TOPIC_IDS_ORDER = gql(`
  mutation UpdateMyTopicIdsOrder($topicIds: [ID!]!) {
    updateUserSettings(sidebar: { myTopicIds: $topicIds }) {
      sidebar {
        myTopicIds
      }
    }
  }
`);

type SidebarSectionsContextType = {
  allSections: Section[];
  sidebarSections: Section[];
  unsavedSections: Section[];
  allTopics: AllTopicsTreeListTopic[];
  myTopics: AllTopicsTreeListTopic[] | null;
  toggleSectionVisibility: (id: string) => void;
  moveSection: (fromIndex: number, toIndex: number) => void;
  deleteSection(id: string): void;
  addSection: (
    sectionData: Pick<Section, 'name'> & Partial<Pick<Section, 'items'>>,
  ) => Section;
  updateSection: (
    data: Pick<Section, 'id'> & Partial<Pick<Section, 'name'>>,
  ) => void;
  addItemToSection: (id: string, item: Section['items'][number]) => void;
  removeItemFromSection: (id: string, itemId: string) => void;
  moveItemInSection: (id: string, fromIndex: number, toIndex: number) => void;
  saveSections: () => Promise<void>;
};

const SidebarSectionsContext =
  React.createContext<SidebarSectionsContextType | null>(null);

export const useSidebarSectionsContext = () => {
  const context = useContext(SidebarSectionsContext);
  if (context === null) {
    throw new Error(
      'useSidebarSectionsContext must be used within SidebarSectionsContext',
    );
  }
  return context;
};

const moveItem = <T,>({
  items,
  from,
  to,
  transform,
}: {
  items: T[];
  from: number;
  to: number;
  transform?: (item: T) => T;
}): T[] => {
  const item = items[from];
  if (!item) return items;
  const clonedItems = items.slice();
  clonedItems.splice(from, 1);
  clonedItems.splice(to, 0, transform ? transform(item) : item);
  return clonedItems;
};

const usePremadeSections = () => {
  const platforms = useExternalPostPlatforms();

  return useMemo(() => {
    const premadeSections: (Section & {
      id: PremadeSectionId;
    })[] = [
      {
        id: PremadeSections.MY_TOPICS,
        name: 'My Topics',
        items: [],
        premade: true,
        visible: true,
      },
      {
        id: PremadeSections.ALL_TOPICS,
        name: 'All Topics',
        items: [],
        premade: true,
        visible: true,
      },
    ];

    if (platforms.length > 0) {
      premadeSections.push({
        id: PremadeSections.INTEGRATIONS,
        name: 'Integrations',
        items: [],
        premade: true,
        visible: true,
      });
    }

    return premadeSections;
  }, [platforms]);
};

interface SidebarSectionsProviderProps {
  children: React.ReactNode;
}

const SidebarSectionsProvider = ({
  children,
}: SidebarSectionsProviderProps) => {
  const { data, refetch, subscribeToMore } = useQuery(
    SIDEBAR_SECTIONS_MODAL_QUERY,
  );

  useSubscription(USER_SETTINGS_UPDATED_SUBSCRIPTION, {
    onData() {
      refetch();
    },
  });

  useEffect(() => {
    subscribeToMore({ document: SECTION_UPDATED_SUBSCRIPTION });
    subscribeToMore({
      document: SECTION_DELETED_SUBSCRIPTION,
      updateQuery(prev, { subscriptionData }) {
        return {
          ...prev,
          currentSession: {
            ...prev.currentSession,
            user: {
              ...prev.currentSession.user,
              sections: prev.currentSession.user.sections.filter(
                (section) =>
                  section.id !== subscriptionData.data.sectionDeleted.id,
              ),
              sidebarSections: prev.currentSession.user.sidebarSections.filter(
                (section) =>
                  section.id !== subscriptionData.data.sectionDeleted.id,
              ),
            },
          },
        };
      },
    });
  }, [subscribeToMore]);

  const [updateSidebarSectionIds] = useMutation(UPDATE_SIDEBAR_SECTION_IDS);
  const [createSidebarSection] = useMutation(CREATE_SIDEBAR_SECTION);
  const [deleteSidebarSection] = useMutation(DELETE_SIDEBAR_SECTION);
  const [updateSidebarSection] = useUpdateSidebarSection();
  const [updateMyTopicIdsOrder] = useMutation(UPDATE_MY_TOPIC_IDS_ORDER);

  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const isModalOpen = selectedSectionId != null;
  const [localSections, setLocalSections] = useState<Section[]>([]);

  const { myTopicIds, refetch: refetchSidebarSettings } = useSidebarSettings();

  const treeTopics = useAllTopics();
  const filteredAllTopics = useMemo(
    () => filterTreeListTopics(treeTopics, isNotDetached),
    [treeTopics],
  );

  const user = useAppSelector((state) => state.session.user);
  const flattenTopics = useAllTopics({ flatten: true });
  const directMembershipTopics = useUserMembershipTopics({ userId: user.id });
  const myTopics = useMembershipTopics(flattenTopics, directMembershipTopics);
  const filteredMyTopics = useMemo(
    () =>
      myTopics
        ? _.sortBy(filterTreeListTopics(myTopics, isNotDetached), (topic) =>
            myTopicIds.indexOf(topic.id),
          )
        : null,
    [myTopics, myTopicIds],
  );
  const [localMyTopics, setLocalMyTopics] = useState(filteredMyTopics);

  useEffect(() => {
    setLocalMyTopics(filteredMyTopics);
  }, [filteredMyTopics, isModalOpen]);

  const sidebarSections = useMemo(
    () => _.compact(data?.currentSession?.user.sidebarSections),
    [data],
  );
  const sidebarSectionsIds = useMemo(
    () => sidebarSections.map((section) => section.id),
    [sidebarSections],
  );

  const premadeSections = usePremadeSections();
  const sections = useMemo<Section[]>(() => {
    return _.sortBy(
      [..._.compact(data?.currentSession?.user.sections), ...premadeSections],
      [
        (section) => {
          const index = sidebarSectionsIds.indexOf(section.id);
          if (index === -1) {
            return Infinity;
          }
          return index;
        },
        (section) =>
          'premade' in section && section.premade
            ? Infinity
            : section.name.toLowerCase(),
      ],
    ).map((section) => ({
      ...section,
      visible: sidebarSectionsIds.includes(section.id),
    }));
  }, [data, sidebarSectionsIds, premadeSections]);

  useEffect(() => {
    setLocalSections(sections);
  }, [sections, isModalOpen]);

  const sidebarSectionsContextValue =
    useMemo((): SidebarSectionsContextType => {
      const allSections = localSections.filter(
        (section) => _.last(section.actions) !== 'delete',
      );

      const localSidebarSections = allSections.filter(
        (section) => section.visible,
      );

      return {
        allTopics: filteredAllTopics,
        myTopics: localMyTopics,
        allSections,
        sidebarSections: localSidebarSections,
        unsavedSections: localSections.filter((section) => {
          if (section.actions && section.actions?.length > 0) {
            // moving an invisible section is not considered an unsaved change
            // we will still generate the 'move' action for it since it could be that
            // the user will make it visible afterwards
            const isInvisibleMove =
              !section.visible &&
              section.actions.every((action) => action === 'move');

            return !isInvisibleMove;
          }

          return false;
        }),
        moveSection(from, to) {
          setLocalSections((currentSections) => {
            return moveItem({
              items: currentSections,
              from,
              to,
              transform: (section) => {
                if (hasDeleteAction(section.actions)) {
                  return section;
                }

                return {
                  ...section,
                  actions: [...(section.actions ?? []), 'move'],
                };
              },
            });
          });
        },
        addSection(sectionData) {
          const newSection: Section = {
            ...sectionData,
            items: sectionData.items ?? [],
            id: _.uniqueId('create'),
            actions: ['create'],
            visible: true,
          };

          setLocalSections((currentSections) => [
            ...currentSections,
            newSection,
          ]);

          return newSection;
        },
        deleteSection(id) {
          setLocalSections((currentSections) => {
            const deletableSection = currentSections.find(
              (section) => section.id === id,
            );

            const filtered = currentSections.filter(
              (section) => section.id !== id,
            );

            if (
              deletableSection == null ||
              hasCreateAction(deletableSection?.actions)
            ) {
              return filtered;
            }

            if (hasDeleteAction(deletableSection.actions)) {
              return [...filtered, deletableSection];
            }

            // we move deleted sections to the end of the list so that
            // the index-based drag and drop still works correctly
            return [
              ...filtered,
              {
                ...deletableSection,
                actions: [...(deletableSection.actions ?? []), 'delete'],
              },
            ];
          });
        },
        addItemToSection(id, item) {
          setLocalSections((currentSections) =>
            currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                return {
                  ...section,
                  items: [...section.items, item],
                  actions: [...(section.actions ?? []), 'update'],
                };
              }

              return section;
            }),
          );
        },
        removeItemFromSection(id, itemId) {
          setLocalSections((currentSections) =>
            currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                return {
                  ...section,
                  items: section.items.filter((item) => item.id !== itemId),
                  actions: [...(section.actions ?? []), 'update'],
                };
              }

              return section;
            }),
          );
        },
        moveItemInSection(id, fromIndex, toIndex) {
          if (id === PremadeSections.MY_TOPICS) {
            setLocalMyTopics((current) =>
              current
                ? moveItem({ items: current, from: fromIndex, to: toIndex })
                : null,
            );

            setLocalSections((currentSections) =>
              currentSections.map((section) => {
                if (section.id === id) {
                  return {
                    ...section,
                    // My Topics can only ever be updated
                    actions: ['update'],
                  };
                }
                return section;
              }),
            );
          } else {
            setLocalSections((currentSections) =>
              currentSections.map((section) => {
                if (section.id === id && !hasDeleteAction(section.actions)) {
                  return {
                    ...section,
                    items: moveItem({
                      items: section.items,
                      from: fromIndex,
                      to: toIndex,
                    }),
                    actions: [...(section.actions ?? []), 'update'],
                  };
                }

                return section;
              }),
            );
          }
        },

        updateSection({ id, ...sectionData }) {
          setLocalSections((currentSections) =>
            currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                return {
                  ...section,
                  ..._.omitBy(sectionData, _.isNil),
                  actions: [...(section.actions ?? []), 'update'],
                };
              }

              return section;
            }),
          );
        },
        saveSections: async () => {
          const savedSections = _.compact(
            await Promise.all(
              localSections.map(async (section): Promise<Section | null> => {
                const wasOriginallyVisible = sidebarSectionsIds.includes(
                  section.id,
                );

                if (hasCreateAction(section.actions)) {
                  try {
                    const result = await createSidebarSection({
                      variables: {
                        name: section.name,
                        itemIds: section.items.map(({ id }) => id),
                      },
                    });

                    if (!result.data) throw new Error('Section not created');

                    return {
                      ...result.data.createSection,
                      visible: section.visible,
                    };
                  } catch {
                    // Section couldn't be created, so we don't want to add it to the sidebar
                    return null;
                  }
                }

                if (_.last(section.actions) === 'delete') {
                  try {
                    const result = await deleteSidebarSection({
                      variables: { id: section.id },
                    });
                    if (!result.data) throw new Error('Section not deleted');

                    // Section was deleted, so we don't store it in the sidebar
                    return null;
                  } catch {
                    return {
                      // Section couldn't be deleted, so we keep it as it was
                      ...section,
                      // in case it was originally visible in the sidebar, we keep it as such
                      visible: wasOriginallyVisible,
                    };
                  }
                }

                if (section.actions?.includes('update')) {
                  try {
                    if (section.id === PremadeSections.MY_TOPICS) {
                      if (localMyTopics) {
                        await updateMyTopicIdsOrder({
                          variables: {
                            topicIds: localMyTopics.map((topic) => topic.id),
                          },
                        });
                        await refetchSidebarSettings();
                      }
                    } else {
                      const result = await updateSidebarSection({
                        variables: {
                          id: section.id,
                          name: section.name,
                          itemIds: section.items.map((item) => item.id),
                        },
                      });

                      if (result.data?.updateSection) {
                        return {
                          ...result.data.updateSection,
                          visible: section.visible,
                        };
                      }
                      return section;
                    }
                  } catch {}
                }

                return section;
              }),
            ),
          );

          const sectionIds = savedSections
            .filter(({ visible }) => visible)
            .map(({ id }) => id);

          if (!_.isEqual(sectionIds, sidebarSectionsIds)) {
            await updateSidebarSectionIds({
              variables: {
                sectionIds,
              },
            });
          }
          await refetch();
        },
        toggleSectionVisibility(id: string) {
          const wasOriginallyVisible = sidebarSectionsIds.includes(id);

          setLocalSections((currentSections) => {
            return currentSections.map((section) => {
              if (section.id === id && !hasDeleteAction(section.actions)) {
                const { actions } = section;
                const nextActions = (() => {
                  if (actions) {
                    if (wasOriginallyVisible && actions.includes('hide')) {
                      return actions.filter(
                        (action) => action !== 'hide',
                      ) as typeof actions;
                    }

                    if (!wasOriginallyVisible && actions.includes('show')) {
                      return actions.filter(
                        (action) => action !== 'show',
                      ) as typeof actions;
                    }
                  }

                  return [
                    ...(actions ?? []),
                    section.visible ? 'hide' : 'show',
                  ] as CreateActionSequence;
                })();

                return {
                  ...section,
                  visible: !section.visible,
                  actions: nextActions,
                };
              }

              return section;
            });
          });
        },
      };
    }, [
      localSections,
      filteredAllTopics,
      localMyTopics,
      sidebarSectionsIds,
      updateSidebarSectionIds,
      createSidebarSection,
      deleteSidebarSection,
      updateSidebarSection,
      updateMyTopicIdsOrder,
      refetchSidebarSettings,
      refetch,
    ]);

  return (
    <SidebarSectionsContext.Provider value={sidebarSectionsContextValue}>
      {children}
    </SidebarSectionsContext.Provider>
  );
};

export default SidebarSectionsProvider;
